/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

// Ant Design
import {
  Row,
  Col,
  Button,
  Card,
  Typography,
  Divider,
  Spin,
  Tag,
  message,
  Input,
} from "antd";
import { TeamOutlined, FormOutlined } from "@ant-design/icons";
import { Gauge } from "@ant-design/plots";

// Custom Components
import { PageHeaderLayout } from "../../../shared/layout";
import { ButtonText } from "../../../shared/globalStyling/styledText";

// Demographic Components
import CategoryList from "./CategoryList";
import SubCategoryList from "./SubCategoryList";
import SelectedMetricList from "./SelectedMetricList";
import SearchResultsView from "./SearchResultsView";

// Modals
import AudienceBuilderSaveModal from "./Modals/AudienceBuilderSaveModal";
import GenericMetric from "./Metrics/GenericMetric";

// Constants
import { graphConfiguration } from "../../../../core/utils/constants/demographicConstants";

// Utils
import { filterAndMapCategories } from "../utils/searchUtils";
import { transformAudienceSchema } from "../utils/transformAudienceSchema";
import {
  cardBodyStyle,
  categoryList,
  searchResultList,
  subCategoryList,
  gaugeStyle,
  sideBarCardStyle,
  sideBarTitleStyle,
} from "../styling/inline-style";
import "../styling/styles.css";

const { Text } = Typography;

const AudienceBuilderPage = props => {
  const {
    history,
    audienceSchema,
    submitAudience,
    refetchAudienceDemographs,
    refetchAudienceSchema,
    fetchAudienceCount,
    loadingAudienceSchema,
  } = props;

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState();
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [graphStateConfig, setGraphStateConfig] = useState(graphConfiguration);
  const [reachCount, setReachCount] = useState(0);
  const [metricValue, setMetricValue] = useState(null);
  const [searchValues, setSearchValues] = useState(null);
  const [groupedCategories, setGroupedCategories] = useState([]);

  const onCategoryClick = category => {
    if (category === selectedCategory) {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSubCategories(null);
      return;
    }

    setSelectedCategory(category);
    setSubCategories(category.datapoints);
  };

  const onSubCategoryClick = subCategory => {
    setSelectedSubCategory(subCategory);
    mapMetrics(subCategory);
    setMetricValue(null);
  };

  const mapMetrics = metrics => {
    const flattenedArray = metrics.mappings.flatMap(obj => Object.values(obj));
    setMetrics(
      flattenedArray.map(obj => {
        return {
          name: metrics.name,
          key: obj,
          type: metrics.type,
        };
      })
    );
  };

  const handleSelectedMetric = metric => {
    setMetricValue(null);
    const { key, value } = metric;
    const dimensions =
      audienceSchema.internalMicroserviceAudienceSchema.dimensions;

    const selectedSubCategory = categories
      .flatMap(item => item.datapoints)
      .find(
        datapoint =>
          Array.isArray(datapoint.mappings) &&
          datapoint.mappings.some(mapping => mapping.statara === metric.key)
      );

    setSelectedSubCategory(selectedSubCategory);

    const response = dimensions.find(
      item =>
        Array.isArray(item.mappings) &&
        item.mappings.some(mapping => mapping.statara === metric.key)
    );

    const category = categories.find(
      category => category.name === response.category.name
    );

    setSubCategories(category.datapoints);

    setSelectedCategory(response.category);
    setMetrics([
      {
        name: response.datapoint,
        key: key,
        type: response.type,
      },
    ]);
    setMetricValue(value);
  };

  const processDeviceCount = async metrics => {
    let response = { data: { getAudienceCount: { count: 0 } } };
    if (metrics.length > 0) {
      response = await fetchAudienceCount(metrics);
    }

    const audienceCountData = response.data.getAudienceCount;
    const totalDeviceCount = audienceCountData.count;
    const percetage = (totalDeviceCount * 100) / 100000000;

    setReachCount(totalDeviceCount);

    if (totalDeviceCount < 10000) {
      setGraphStateConfig(prevState => ({
        ...prevState,
        percent: percetage / 100,
        content: {
          style: {
            fontSize: "20px",
            lineHeight: "20px",
            color: "#4B535E",
          },
          formatter: () => <Tag color="red">Minimum of 10,0000</Tag>,
        },
        statistic: {
          ...prevState.statistic,
          title: {
            ...prevState.statistic.title,
            formatter: () => totalDeviceCount.toLocaleString() || 0,
          },
        },
      }));
      return;
    } else {
      setGraphStateConfig(prevState => ({
        ...prevState,
        percent: percetage / 100,
        statistic: {
          ...prevState.statistic,
          title: {
            ...prevState.statistic.title,
            formatter: () =>
              totalDeviceCount ? totalDeviceCount.toLocaleString() : 0,
          },
        },
      }));
    }
  };

  const removeAudienceMetric = async metric => {
    const filteredMetrics = selectedMetrics.filter(
      item => item.key !== metric.key
    );

    setSelectedMetrics(filteredMetrics);
    setLoading(true);
    await processDeviceCount(filteredMetrics);
    setLoading(false);
  };

  const updateAudienceMetrics = async metrics => {
    setLoading(true);

    const { matchCharacteristics } = metrics;

    var newMetrics = [];

    const existingMetricIndex = selectedMetrics.findIndex(
      metric => metric.key === metrics.metricValue.key
    );

    if (existingMetricIndex !== -1) {
      selectedMetrics[existingMetricIndex].value = metrics.metricValue.value;
      selectedMetrics[existingMetricIndex].operator = matchCharacteristics;
      selectedMetrics[existingMetricIndex].match = matchCharacteristics === "=";
    } else {
      newMetrics.push({
        match: matchCharacteristics === "=",
        operator: matchCharacteristics,
        value: metrics.metricValue.value,
        label: metrics.metricValue.name,
        key: metrics.metricValue.key,
      });
    }

    const combineMetrics = [...newMetrics, ...selectedMetrics];

    await processDeviceCount(combineMetrics);
    setSelectedMetrics(combineMetrics);

    setLoading(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const validateSubmission = data => {
    if (reachCount < 10000) {
      message.error("Minimum of 10,000 devices required to save audience");
      return false;
    }
    return true;
  };

  // Submit to Save Audience
  const handleSubmit = async data => {
    if (validateSubmission(data) === true) {
      const audienceData = {
        ...data,
        metrics: selectedMetrics,
        reachCount: reachCount,
      };

      await submitAudience(audienceData);
      await refetchAudienceDemographs();

      message.success("Successfully Created an Audience");
      history.push("/demographic");
    }
  };

  const resetList = () => {
    setCategories(Object.values(groupedCategories));
    setSubCategories([]);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
  };

  useEffect(() => {
    refetchAudienceSchema();
  }, [refetchAudienceSchema]);

  useEffect(() => {
    const {
      filteredCategories,
      filteredSubCategories,
    } = filterAndMapCategories(groupedCategories, searchValues);

    if (!filteredCategories.length) {
      resetList();
      return;
    }

    setCategories(filteredCategories);
    setSelectedCategory(filteredCategories[0]);
    setSubCategories(filteredSubCategories);
  }, [searchValues]);

  useEffect(() => {
    const { groupedCategories } = transformAudienceSchema(audienceSchema);

    setGroupedCategories(groupedCategories);
    setCategories(Object.values(groupedCategories));
  }, [audienceSchema]);

  return (
    <>
      <AudienceBuilderSaveModal
        visible={open}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        history={history}
        refetchAudienceDemographs={refetchAudienceDemographs}
      />
      <PageHeaderLayout
        title={"Demographics"}
        titleIcon={<TeamOutlined className="page-header-bar-icon" />}
        rightContent={
          <Row>
            <Col>
              <Button
                onClick={() => {
                  setOpen(true);
                }}
                type="primary"
                ghost
                icon={<FormOutlined />}
              >
                <ButtonText text={"Build New Audience"} />
              </Button>
            </Col>
          </Row>
        }
      />

      <Row>
        <Col span={19} className="col-category-parent">
          <Card
            title={
              <div className="card-header-title-layout">
                EXPLORE AUDIENCE CATEGORIES
                <Input
                  allowClear
                  disabled={loadingAudienceSchema}
                  placeholder="Search..."
                  value={searchValues}
                  onChange={e => setSearchValues(e.target.value)}
                  className="search-input"
                />
              </div>
            }
            bordered={false}
            bodyStyle={cardBodyStyle}
          >
            <Row className="card-body-row-style">
              {!searchValues && (
                <>
                  <Col span={7} className="col-category-list">
                    <Spin spinning={loadingAudienceSchema}>
                      <CategoryList
                        list={categories}
                        selectedCategory={selectedCategory}
                        onCategoryClick={onCategoryClick}
                        titleFontStyle={categoryList.titleFontStyle}
                        listItemStyle={categoryList.listItemStyle}
                        selectedStyle={categoryList.selectedStyle}
                        selectedFontStyle={categoryList.selectedFontStyle}
                      ></CategoryList>
                    </Spin>
                  </Col>
                  {!selectedCategory && (
                    <>
                      <Col span={17} className="col-empty-view">
                        <Text className="empty-view-text">
                          Select categories on the left to browser targeting
                          metrics
                        </Text>
                        <br />
                        <Text className="empty-view-text-description">
                          When you are satisfied, name your audience to save it
                          for use
                        </Text>
                      </Col>
                    </>
                  )}
                  {selectedCategory && (
                    <>
                      <Col span={7} className="col-sub-category-list ">
                        <SubCategoryList
                          list={subCategories}
                          selectedCategory={selectedSubCategory}
                          loading={false}
                          listItemStyle={subCategoryList.listItemStyle}
                          titleFontStyle={subCategoryList.titleFontStyle}
                          selectedStyle={subCategoryList.selectedStyle}
                          selectedFontStyle={subCategoryList.selectedFontStyle}
                          onCategoryClick={onSubCategoryClick}
                          setSelectedSubCategory={setSelectedSubCategory}
                        ></SubCategoryList>
                      </Col>
                    </>
                  )}
                </>
              )}
              {searchValues && (
                <Col span={14} className="search-results-view-style">
                  <SearchResultsView
                    searchValues={searchValues}
                    subCategories={subCategories}
                    selectedCategory={selectedSubCategory}
                    selectedStyle={searchResultList.selectedStyle}
                    selectedFontStyle={searchResultList.selectedFontStyle}
                    titleFontStyle={searchResultList.titleFontStyle}
                    listItemStyle={searchResultList.listItemStyle}
                    onCategoryClick={onSubCategoryClick}
                    setSearchValues={setSearchValues}
                  />
                </Col>
              )}
              {selectedCategory && selectedSubCategory && (
                <Col span={10}>
                  <GenericMetric
                    metrics={metrics}
                    updateAudienceMetrics={updateAudienceMetrics}
                    fetchAudienceCount={fetchAudienceCount}
                    removeAudienceMetric={removeAudienceMetric}
                    loading={loading}
                    currentMetricValue={metricValue}
                    selectedSubCategory={selectedSubCategory}
                  ></GenericMetric>
                </Col>
              )}
            </Row>
          </Card>
        </Col>

        <Col span={5}>
          <Card bordered={false} style={sideBarCardStyle}>
            <Gauge {...graphStateConfig} style={gaugeStyle} />
            <Divider></Divider>
            <Typography style={sideBarTitleStyle}>ANALYTIC</Typography>
            <SelectedMetricList
              list={selectedMetrics}
              handleSelectedMetric={handleSelectedMetric}
            ></SelectedMetricList>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AudienceBuilderPage;
